import React, { useState, useEffect, useRef } from "react";
import { theme } from "../../theme";

const Timeline = ({ items }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const itemRefs = useRef([]);

  // 1. SYNC WITH URL HASH (Deep Linking)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      // Matches #phase-1, #phase-2, etc.
      const match = hash.match(/#phase-(\d+)/);
      if (match) {
        const index = parseInt(match[1], 10) - 1; // 1-based ID to 0-based index
        if (items[index]) {
          setActiveIndex(index);
          setExpandedIndex(index);
          // Optional: slight focus management or scroll could go here
        }
      }
    };

    // Check on mount
    handleHashChange();

    // Listen for changes
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [items]);

  // 2. INTERACTION HANDLERS
  const updateSelection = (index) => {
    setActiveIndex(index);
    // Update URL without scrolling page jump
    window.history.replaceState(null, null, `#phase-${index + 1}`);
  };

  const handleToggle = (e, index) => {
    e.stopPropagation();
    const isExpanding = expandedIndex !== index;
    setExpandedIndex(isExpanding ? index : null);

    // If we are expanding a different phase, make it active too
    if (isExpanding) updateSelection(index);
  };

  // 3. KEYBOARD NAVIGATION (Exec-Proofing)
  const handleKeyDown = (e, index) => {
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      const next = Math.min(index + 1, items.length - 1);
      itemRefs.current[next]?.focus();
      updateSelection(next);
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      const prev = Math.max(index - 1, 0);
      itemRefs.current[prev]?.focus();
      updateSelection(prev);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleToggle(e, index);
    }
  };

  /* --- CSS SYSTEM --- */
  const css = `
    .timeline-container { position: relative; padding-top: 1rem; }
    
    /* ITEM ROW */
    .timeline-item { 
      display: flex; gap: 1.5rem; position: relative; padding-bottom: 2.5rem; 
      outline: none; /* Focus handled visually */
    }
    .timeline-item:last-child { padding-bottom: 0; }
    
    /* VERTICAL LINE (Progress Aware) */
    .timeline-connector { 
      position: absolute; top: 2.5rem; left: 1rem; bottom: 0; width: 2px; 
      background: ${theme.colors.lines}; z-index: 0; 
      transition: background 0.3s ease;
    }
    .timeline-item.completed .timeline-connector { background: ${
      theme.colors.primaryBlue
    }; }
    .timeline-item.upcoming .timeline-connector { background: ${
      theme.colors.lines
    }; border-left: 2px dashed #ccc; width: 0; }
    .timeline-item:last-child .timeline-connector { display: none; }
    
    /* MARKER (Status Aware) */
    .timeline-marker { 
      position: relative; z-index: 1; flex-shrink: 0; 
      width: 2.25rem; height: 2.25rem; 
      border-radius: 50%; border: 2px solid ${theme.colors.lines}; 
      background: #fff; 
      display: flex; align-items: center; justify-content: center; 
      font-weight: 700; color: ${theme.colors.ink70 || "#666"}; 
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    /* Active State (Pulsing) */
    .timeline-item.active .timeline-marker { 
      border-color: ${theme.colors.primaryBlue}; 
      color: ${theme.colors.primaryBlue};
      background: #fff;
      box-shadow: 0 0 0 4px ${theme.colors.accentBlue || "#EBF3FF"};
      transform: scale(1.1);
    }

    /* Completed State (Filled) */
    .timeline-item.completed .timeline-marker {
      background: ${theme.colors.primaryBlue};
      border-color: ${theme.colors.primaryBlue};
      color: #fff;
    }

    /* Keyboard Focus Ring */
    .timeline-item:focus-visible .timeline-marker {
      outline: 2px solid ${theme.colors.primaryBlue};
      outline-offset: 4px;
    }
    
    /* CONTENT CARD */
    .timeline-card { 
      border: 1px solid ${theme.colors.lines}; 
      border-radius: 12px; padding: 1.5rem; 
      background: ${theme.colors.cardBg || "#fff"}; 
      transition: all 0.2s ease; cursor: pointer; 
      flex: 1;
    }
    .timeline-item.active .timeline-card { 
      border-color: ${theme.colors.primaryBlue}; 
      box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1); 
    }
    .timeline-item.upcoming .timeline-card { opacity: 0.8; }
    .timeline-item:hover .timeline-card { border-color: ${
      theme.colors.accentBlue || "#99ccff"
    }; }

    /* TOGGLE BTN */
    .details-toggle { 
      background: none; border: none; padding: 0.5rem; 
      color: ${theme.colors.primaryBlue}; font-weight: 600; 
      cursor: pointer; display: flex; align-items: center; gap: 0.5rem; 
      border-radius: 4px;
    }
    .details-toggle:focus-visible { background: #f0f9ff; outline: none; }

    /* ANIMATIONS & PREF-REDUCED-MOTION */
    @keyframes slideDown { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
    
    @media (prefers-reduced-motion: no-preference) {
      .animate-enter { animation: slideDown 0.3s ease-out forwards; }
      .timeline-marker, .timeline-connector, .timeline-card { transition-duration: 0.3s; }
    }
  `;

  return (
    <div className="timeline-container">
      <style>{css}</style>

      {items.map((item, index) => {
        // Status Logic
        const isActive = activeIndex === index;
        const isCompleted = index < activeIndex;
        const isUpcoming = index > activeIndex;
        const isExpanded = expandedIndex === index;

        const statusClass = isActive
          ? "active"
          : isCompleted
          ? "completed"
          : "upcoming";

        // Data Handling
        const title = (item.phaseName || item.phase || "").split(":")[0];
        const subTitle = (item.phaseName || item.phase || "").split(":")[1];

        return (
          <div
            key={index}
            ref={(el) => (itemRefs.current[index] = el)}
            className={`timeline-item ${statusClass}`}
            onClick={() => updateSelection(index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            role="button"
            tabIndex={0}
            aria-label={`Phase ${index + 1}: ${title}. Status: ${statusClass}`}
            aria-current={isActive ? "step" : undefined}
          >
            <div className="timeline-connector" aria-hidden="true" />

            <div className="timeline-marker" aria-hidden="true">
              {isCompleted ? "✓" : index + 1}
            </div>

            <div style={{ flex: 1, paddingTop: "0.25rem" }}>
              <div className="timeline-card">
                {/* Header Row */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "1rem",
                  }}
                >
                  <div>
                    <h3
                      style={{
                        fontSize: "1.25rem",
                        margin: 0,
                        color: theme.colors.navyText,
                      }}
                    >
                      {title}
                    </h3>
                    {subTitle && (
                      <div
                        style={{
                          fontSize: "1rem",
                          fontWeight: 500,
                          marginTop: "0.25rem",
                          color: theme.colors.navyText,
                        }}
                      >
                        {subTitle}
                      </div>
                    )}
                  </div>
                  <span
                    style={{
                      backgroundColor: isActive
                        ? theme.colors.primaryBlue
                        : isCompleted
                        ? "#DCFCE7"
                        : "#F3F4F6",
                      color: isActive
                        ? "#fff"
                        : isCompleted
                        ? "#166534"
                        : theme.colors.navyText,
                      padding: "0.25rem 0.75rem",
                      borderRadius: "99px",
                      fontSize: "0.75rem",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {isCompleted ? "Complete" : item.duration}
                  </span>
                </div>

                {/* Purpose */}
                <p
                  style={{
                    marginBottom: "1rem",
                    fontStyle: "italic",
                    color: "#666",
                    lineHeight: "1.5",
                  }}
                >
                  {item.purpose}
                </p>

                {/* Footer / Toggle */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderTop:
                      isActive || isExpanded
                        ? `1px solid ${theme.colors.lines}`
                        : "none",
                    paddingTop: isActive || isExpanded ? "1rem" : 0,
                  }}
                >
                  <div style={{ fontSize: "0.9rem", color: "#666" }}>
                    Primary Focus:{" "}
                    <strong
                      style={{
                        color: isActive
                          ? theme.colors.primaryBlue
                          : theme.colors.navyText,
                      }}
                    >
                      {item.deliverablesHeading || "Key Outcomes"}
                    </strong>
                  </div>
                  <button
                    className="details-toggle"
                    onClick={(e) => handleToggle(e, index)}
                    aria-expanded={isExpanded}
                    aria-controls={`phase-details-${index}`}
                    tabIndex={0}
                  >
                    {isExpanded ? "Hide Details" : "View Details"}
                    <span
                      aria-hidden="true"
                      style={{ fontSize: "1.2em", lineHeight: 1 }}
                    >
                      {isExpanded ? "−" : "+"}
                    </span>
                  </button>
                </div>

                {/* Expanded Content Area */}
                {isExpanded && (
                  <div
                    id={`phase-details-${index}`}
                    className="animate-enter"
                    style={{ marginTop: "1.5rem" }}
                  >
                    {/* Deliverables Box */}
                    <div
                      style={{
                        backgroundColor: "#F9FAFB",
                        padding: "1.25rem",
                        borderRadius: "8px",
                      }}
                    >
                      <h4
                        style={{
                          margin: "0 0 1rem 0",
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          color: "#666",
                        }}
                      >
                        Deliverables
                      </h4>
                      <ul
                        style={{
                          margin: 0,
                          padding: 0,
                          listStyle: "none",
                          columns: 2,
                          columnGap: "2rem",
                        }}
                      >
                        {item.deliverables.map((d, i) => (
                          <li
                            key={i}
                            style={{
                              marginBottom: "0.5rem",
                              fontSize: "0.95rem",
                              color: theme.colors.navyText,
                              display: "flex",
                              gap: "0.5rem",
                            }}
                          >
                            <span
                              style={{ color: theme.colors.primaryBlue }}
                              aria-hidden="true"
                            >
                              •
                            </span>
                            {d}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Subsections */}
                    {item.subSections && item.subSections.length > 0 && (
                      <div style={{ marginTop: "1.5rem" }}>
                        {item.subSections.map((sub, j) => (
                          <div key={j} style={{ marginBottom: "1rem" }}>
                            <strong
                              style={{
                                color: theme.colors.primaryBlue,
                                fontSize: "0.95rem",
                                display: "block",
                                marginBottom: "0.25rem",
                              }}
                            >
                              {sub.title}
                            </strong>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "0.9rem",
                                color: "#666",
                              }}
                            >
                              {sub.bullets ? sub.bullets.join(", ") : ""}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Timeline;
